'use client';

import { ReactNode, useState } from 'react';

import {
    Box,
    BoxContent,
    Button,
    Columns,
    ColumnsItem,
    Icon,
    IconName,
    IconSize,
    Link,
    Modal,
    ModalFooter,
    Sticker,
    Text,
    TextLevels,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react-ts';

import { getAppConfig } from '@services/config';

import { PlanType } from '@app-types/product';

import { Cms } from '@components/cms';
import { SSIBlock } from '@components/ssi-block';

import { useHome } from '@landing/hooks';

import { SummerEditionFlags, SummerEditionList } from './summer-edition';

interface ProductDetailProps {
    product: PlanType | null;
}

export function ProductDetail({ product }: Readonly<ProductDetailProps>) {
    const { selectedOption } = useHome();
    const appConfig = getAppConfig();
    const [isModalOpen, setIsModalOpen] = useState(false);
    if (!product) {
        return null;
    }
    const is5gProduct = !!selectedOption?.gencode || product.maxConnections === '5g';

    const isSummerEdition = product.gencode === appConfig.offers.summerEditionGencode;

    return (
        <Box shadowless>
            <BoxContent background={TrilogyColor.GREY10} className='is-flex is-vertical gap-medium'>
                <Feature iconName={is5gProduct ? IconName.UI_5G : IconName.UI_4G}>{product.networkText}</Feature>

                <Feature iconName={IconName.AIR_TRAVEL}>
                    {isSummerEdition ? (
                        <>
                            <Sticker small className='byou-sticker'>
                                Summer edition
                            </Sticker>
                            <p>
                                <strong>{product.roamingEnvelope}</strong>
                                <span>
                                    &nbsp;{product.fromAbroad}&nbsp;
                                    <SummerEditionFlags />
                                </span>
                            </p>
                        </>
                    ) : (
                        <>
                            <strong>{product.roamingEnvelope} </strong>
                            <Cms as='span' content={product.fromAbroad} />
                        </>
                    )}
                </Feature>

                <Feature iconName={IconName.PHONE_INFINITY}>
                    <Cms as='span' content={product.smsMms} />
                </Feature>
                <Feature iconName={IconName.HAND_MOBILE}>
                    <Cms as='span' content="Assistance 24h/24 via l'application" />
                </Feature>

                <Link role='button' onClick={() => setIsModalOpen(true)}>
                    *&nbsp;Voir les destinations {isSummerEdition ? 'incluses sur Summer edition' : 'en Europe'}
                </Link>
                <Modal
                    closeIcon
                    active={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={isSummerEdition ? 'Destinations incluses sur Summer edition' : 'Destinations en Europe'}
                    contentClassNames='has-text-left'
                >
                    {isSummerEdition ? (
                        <SummerEditionList />
                    ) : (
                        <SSIBlock
                            url='/partials/mobiles/banniere_forfaits-mobiles_destinations_europe'
                            tagId='destinations_europe'
                        />
                    )}

                    <ModalFooter className='is-flex is-justified-end'>
                        <Button
                            onClick={() => setIsModalOpen(false)}
                            variant={TrilogyColor.PRIMARY}
                            className='has-byou-background'
                        >
                            Fermer
                        </Button>
                    </ModalFooter>
                </Modal>
            </BoxContent>
        </Box>
    );
}

const Feature = ({ iconName, children }: { iconName: IconName; children?: ReactNode }) => {
    return (
        <Columns mobile marginless verticalCentered multiline>
            <ColumnsItem narrow className='is-paddingless is-flex'>
                <Icon size={IconSize.SMALL} name={iconName} className='me-3' />
            </ColumnsItem>
            <ColumnsItem className='is-paddingless'>
                <Text level={TextLevels.TWO} marginless typo={TypographyAlign.TEXT_LEFT}>
                    {children}
                </Text>
            </ColumnsItem>
        </Columns>
    );
};
